// «Claude Code» в настройках: на какой версии работает агент, вышла ли новая, и обновление с проверкой.
// Новая версия ставится рядом сама и сообщает свои модели; обновляется только после «Проверить и обновить»:
// платформа проверяет протокол и даёт агенту проверочные задания на новой версии, и лишь если всё зелёное —
// переключает агента, дождавшись, пока он закончит текущую работу. Откат — одной кнопкой.
import { Button, Card, Chip, type Tone, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, FlaskConical, PackageCheck, RefreshCw, RotateCcw, Sparkles, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';

interface CheckRow {
  /** cli — сам Claude Code (протокол), agent — проверочные задания агенту. */
  group?: 'cli' | 'agent';
  name: string;
  ok: boolean;
  detail?: string;
}
interface Release {
  version: string;
  status: 'found' | 'ready' | 'testing' | 'passed' | 'failed' | 'deploying' | 'deployed' | 'rolled_back';
  newModels: Array<{ id: string; label: string }> | null;
  report: { checks?: CheckRow[]; ms?: number } | null;
  error: string | null;
  previousVersion: string | null;
  foundAt: string;
  testedAt: string | null;
  deployedAt: string | null;
  rolledBackAt: string | null;
  rollbackMs: number | null;
}
interface GoldenRow {
  id: string;
  title: string;
  ok: boolean;
  detail: string;
}
interface CliState {
  current: { version: string | null; custom: string | null } | null;
  releases: Release[];
  rollback: { from: string; to: string | null } | null;
  golden: { startedAt: string; finishedAt: string | null; results: GoldenRow[]; error?: string } | null;
  busy: string | null;
  goldenRunning: boolean;
}

const STATUS: Record<Release['status'], { label: string; tone: Tone }> = {
  found: { label: 'Ставится рядом', tone: 'info' },
  ready: { label: 'Готова к проверке', tone: 'accent' },
  testing: { label: 'Проверяется', tone: 'info' },
  passed: { label: 'Проверка пройдена', tone: 'success' },
  deploying: { label: 'Ждёт, пока агент освободится', tone: 'info' },
  deployed: { label: 'Работает', tone: 'success' },
  failed: { label: 'Не прошла проверку', tone: 'danger' },
  rolled_back: { label: 'Откачена', tone: 'warning' },
};

function CheckItems({ rows }: { rows: CheckRow[] }) {
  return (
    <ul className="check-list">
      {rows.map((c) => (
        <li key={c.name} className={c.ok ? '' : 'bad'}>
          {c.ok ? <Check aria-label="пройдено" /> : <X aria-label="не пройдено" />}
          <span>
            {c.name}
            {c.detail && !c.ok && <em> — {c.detail}</em>}
          </span>
        </li>
      ))}
    </ul>
  );
}

const GROUP = { cli: 'Claude Code', agent: 'Проверочные задания' } as const;

/** Итог по группам «пройдено N из M»; не прошедшее — сразу, всё остальное — по нажатию. */
function Checks({ rows }: { rows: CheckRow[] }) {
  const groups = (['cli', 'agent'] as const)
    .map((g) => ({ g, list: rows.filter((r) => (r.group ?? 'agent') === g) }))
    .filter((x) => x.list.length);
  const bad = rows.filter((r) => !r.ok);
  return (
    <div className="check-block">
      <span>
        {groups
          .map(({ g, list }) => `${GROUP[g]}: ${list.filter((r) => r.ok).length} из ${list.length}`)
          .join(' · ')}
      </span>
      {bad.length > 0 && <CheckItems rows={bad} />}
      <details>
        <summary>Все проверки</summary>
        {groups.map(({ g, list }) => (
          <div key={g}>
            {groups.length > 1 && <b>{GROUP[g]}</b>}
            <CheckItems rows={list} />
          </div>
        ))}
      </details>
    </div>
  );
}

export function ClaudeCodeCard() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<'check' | 'update' | 'rollback' | 'golden' | null>(null);
  const q = useQuery({
    queryKey: ['system', 'cli'],
    queryFn: () => api<CliState>('/api/system/cli'),
    // Пока идёт проверка или выкатка — обновляем чаще
    refetchInterval: (query) => {
      const d = query.state.data;
      return d?.busy || d?.goldenRunning ? 5_000 : 60_000;
    },
  });

  const act = async (kind: NonNullable<typeof busy>, path: string, done?: (d: CliState) => string | null) => {
    setBusy(kind);
    try {
      const d = await api<CliState>(path, {});
      qc.setQueryData(['system', 'cli'], d);
      const msg = done?.(d);
      if (msg) toast(msg);
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const d = q.data;
  const cur = d?.current;
  const rel = d?.releases[0];
  // Свежая версия, которую ещё не выкатили (или выкатили — тогда показываем итог и откат)
  const showRel = rel && rel.version !== cur?.version ? rel : rel?.status === 'deployed' ? rel : null;
  const running = !!d?.busy || rel?.status === 'testing' || rel?.status === 'deploying';
  const golden = d?.golden;

  return (
    <Card
      title="Claude Code"
      action={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw />}
          disabled={busy !== null || running}
          onClick={() =>
            act('check', '/api/system/cli/check', (x) =>
              x.releases[0] && x.releases[0].version !== x.current?.version
                ? `Есть версия ${x.releases[0].version}`
                : 'Новых версий нет',
            )
          }
        >
          {busy === 'check' ? 'Проверяю…' : 'Проверить обновления'}
        </Button>
      }
    >
      {q.isPending && <div className="skeleton" style={{ height: 40 }} />}
      {q.isError && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="muted">Не удалось загрузить. {(q.error as Error).message}</span>
          <Button size="sm" onClick={() => q.refetch()}>
            Повторить
          </Button>
        </div>
      )}
      {d && (
        <ul className="backup-list">
          <li>
            <PackageCheck aria-hidden="true" />
            <div className="min-w-0">
              <b>Агент работает на версии {cur?.version ?? '—'}</b>
              <span>
                {!cur
                  ? 'Версия появится, когда подключится сервис агента.'
                  : cur.custom
                    ? 'Обновлена платформой после проверки.'
                    : 'Версия из сборки сервера.'}
              </span>
            </div>
          </li>

          {showRel && (
            <li>
              <Sparkles aria-hidden="true" />
              <div className="min-w-0">
                <div className="rel-head">
                  <b>
                    {showRel.status === 'deployed' ? 'Последнее обновление' : 'Вышла версия'}{' '}
                    {showRel.version}
                  </b>
                  <Chip tone={STATUS[showRel.status].tone} dot={showRel.status === 'deployed'}>
                    {STATUS[showRel.status].label}
                  </Chip>
                </div>
                <span>
                  {showRel.status === 'deployed' && showRel.deployedAt
                    ? `Обновлено ${when(showRel.deployedAt)}${showRel.previousVersion ? `, до этого была ${showRel.previousVersion}` : ''}.`
                    : showRel.status === 'rolled_back' && showRel.rolledBackAt
                      ? `Откачена ${when(showRel.rolledBackAt)}${showRel.rollbackMs !== null ? ` за ${Math.max(1, Math.round(showRel.rollbackMs / 1000))} с` : ''}.`
                      : `Найдена ${when(showRel.foundAt)}.`}
                  {showRel.newModels?.length
                    ? ` Новые модели: ${showRel.newModels.map((m) => m.label).join(', ')} — сами не включаются, модель выбираете вы.`
                    : showRel.newModels
                      ? ' Новых моделей нет.'
                      : ''}
                </span>
                {showRel.status === 'testing' && (
                  <span>
                    Проверяю протокол и даю агенту проверочные задания на новой версии — обычно 15–30 минут.
                  </span>
                )}
                {showRel.status === 'deploying' && (
                  <span>
                    Проверка пройдена. Новые задачи ждут, текущие доделываются — затем агент перейдёт на новую
                    версию.
                  </span>
                )}
                {showRel.error && <span className="notice error">{showRel.error}</span>}
                {showRel.report?.checks?.length ? <Checks rows={showRel.report.checks} /> : null}
              </div>
            </li>
          )}

          <li>
            <FlaskConical aria-hidden="true" />
            <div className="min-w-0">
              <b>Проверка агента</b>
              <span>
                {d.goldenRunning
                  ? 'Идёт: агент решает проверочные задания, платформа сверяет ответы со своими данными.'
                  : golden?.finishedAt
                    ? `${when(golden.finishedAt)}${golden.error ? ` · ${golden.error}` : ''}`
                    : 'Проверочные задания: цифры из базы, поиск в памяти, советы через шлюз, команды в данных сети, решения по рекламе, тексты, итоги и честный ответ, когда данных нет. Ответы сверяются с данными платформы.'}
              </span>
              {!d.goldenRunning && golden?.results.length ? (
                <Checks
                  rows={golden.results.map((r) => ({
                    group: 'agent',
                    name: r.title,
                    ok: r.ok,
                    detail: r.detail,
                  }))}
                />
              ) : null}
            </div>
          </li>
        </ul>
      )}

      {d && (
        <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 14 }}>
          {showRel && ['found', 'ready', 'failed', 'rolled_back'].includes(showRel.status) && (
            <Button
              variant="primary"
              icon={<PackageCheck />}
              disabled={busy !== null || running}
              onClick={() =>
                act(
                  'update',
                  `/api/system/cli/${showRel.version}/update`,
                  () => 'Проверка началась — обновлю, только если всё пройдёт',
                )
              }
            >
              {showRel.status === 'failed' ? 'Проверить снова' : 'Проверить и обновить'}
            </Button>
          )}
          {d.rollback && (
            <Button
              icon={<RotateCcw />}
              disabled={busy !== null || running}
              onClick={() =>
                act(
                  'rollback',
                  '/api/system/cli/rollback',
                  (x) => `Вернул версию ${x.current?.version ?? ''}`,
                )
              }
            >
              {busy === 'rollback'
                ? 'Возвращаю…'
                : `Вернуть ${d.rollback.to ?? rel?.previousVersion ?? 'прошлую версию'}`}
            </Button>
          )}
          <Button
            variant="ghost"
            icon={<FlaskConical />}
            disabled={busy !== null || running || d.goldenRunning}
            onClick={() => act('golden', '/api/system/cli/golden', () => 'Проверка агента началась')}
          >
            Проверить агента
          </Button>
        </div>
      )}
      <p className="muted" style={{ margin: '14px 0 0', fontSize: 'var(--fs-sm)' }}>
        Раз в день платформа смотрит, вышла ли новая версия, ставит её рядом с работающей и сообщает вам.
        Агент переходит на неё только после вашего «Проверить и обновить» и только если проверка пройдена.
      </p>
    </Card>
  );
}
